import React from 'react';
import type { WCardLiteView } from '@zola/svc-web-api-ts-client';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { useDispatch } from 'react-redux';
import { CARD_TYPE_MAP, LeadCardType } from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { getCardSuitePath } from '@zola/zola-ui/src/paper/cards/util/getCardUrl';
import { openModal } from 'cards/actions/modalActions';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { trackEvent } from '@zola-helpers/client/dist/es/tracking/trackingHelper';
import { trackProductClicked } from '@zola/tracking-contracts/src/tracking';
import { mapCardTypeToBusinessCategory } from '@zola/zola-ui/src/paper/cards/util/trackingHelpers';
import { SAMPLES_MODAL_DEFAULT_OPTIONS } from 'cards/components/common/modals/SamplesModal/defaultOptions';

import styles from './CardTile.module.less';

interface CardTileActionsProps {
  color: string;
  card: WCardLiteView;
  priceCents?: number;
  thumbnail?: string;
  position: number;
}

const cardTypesSupportingFreeSample = [CARD_TYPE_MAP.invitation, CARD_TYPE_MAP.std];

export const CardTileActions: React.FC<CardTileActionsProps> = ({
  card = {},
  color,
  priceCents = 0,
  thumbnail,
  position,
}) => {
  const dispatch = useDispatch();
  const handleCustomizeClick = (e: React.FormEvent<EventTarget>) => {
    e.stopPropagation();
    e.preventDefault();
    const {
      cardType = '',
      uuid = '',
      designDisplayName = '',
      name = '',
      imageUrlsByColor = {},
    } = card;
    const mappedTrackingType = mapCardTypeToBusinessCategory(cardType as string);
    const trackingData = {
      business_unit: 'PAPER',
      business_category: mappedTrackingType,
      entity_type: 'PAPER',
      card_suite_uuid: imageUrlsByColor[color]?.defaultVariationUuid || '',
      sku: uuid,
      category: mappedTrackingType,
      name,
      brand: designDisplayName,
      price: +(priceCents / 100).toFixed(2),
      quantity: 1,
      image_url: thumbnail || '',
      position: position || 0,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:disable-next-line : Sku number | string
    trackProductClicked(trackingData);

    const customizationLink = getCardSuitePath(
      (card.cardType as unknown) as LeadCardType,
      card.uuid as string
    );
    window.location.href = customizationLink;
  };

  const openSamplesModal = (e: React.FormEvent<EventTarget>) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent('CTA Clicked', {
      business_unit: BUSINESS_UNIT.INVITATIONS,
      cta_type: 'BUTTON',
      cta: 'PLP_CARD_SAMPLE_HOVER_BUTTON',
    });

    dispatch(
      openModal(
        'SAMPLES_MODAL',
        {
          entryPoint: 'PLP_SUITES',
          cardNameList: [card.name],
          cardSuiteUUIDList: [card.uuid],
          isMagnet: false,
          selectedColorSet: {
            [card.uuid || '']: color,
          },
        },
        SAMPLES_MODAL_DEFAULT_OPTIONS
      )
    );
  };

  const showFreeSample =
    card.cardType && typeof card.cardType === 'string'
      ? cardTypesSupportingFreeSample.includes(card.cardType)
      : false;

  return (
    <div className={styles.cardActions} onClick={handleCustomizeClick}>
      <div className={styles.cardMobileActions} onClick={handleCustomizeClick} />
      <div className={styles.cardDesktopActions}>
        {showFreeSample ? (
          <ButtonV3 variant="secondary" onClick={openSamplesModal}>
            Personalize Free Sample
          </ButtonV3>
        ) : null}
        <ButtonV3 variant="primary" onClick={handleCustomizeClick}>
          Customize
        </ButtonV3>
      </div>
    </div>
  );
};
